import React from 'react';
import { matchPath, useLocation, useResolvedPath } from 'react-router-dom';
import {
  BoltIcon,
  BuildingLibraryIcon,
  CircleStackIcon,
  ClipboardDocumentCheckIcon,
  Cog6ToothIcon,
  CubeIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  FolderIcon,
  HomeIcon,
  PhoneIcon,
  RectangleStackIcon,
  SparklesIcon,
  TicketIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import {
  BoltIcon as BoltIconSolid,
  BuildingLibraryIcon as BuildingLibraryIconSolid,
  CircleStackIcon as CircleStackIconSolid,
  ClipboardDocumentCheckIcon as ClipboardDocumentCheckIconSolid,
  Cog6ToothIcon as Cog6ToothIconSolid,
  CubeIcon as CubeIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
  EnvelopeIcon as EnvelopeIconSolid,
  FolderIcon as FolderIconSolid,
  HomeIcon as HomeIconSolid,
  PhoneIcon as PhoneIconSolid,
  RectangleStackIcon as RectangleStackIconSolid,
  SparklesIcon as SparklesIconSolid,
  TicketIcon as TicketIconSolid,
  UserGroupIcon as UserGroupIconSolid
} from '@heroicons/react/24/solid';

import { Action, NavigationItem, Resource } from '~/api/graphql';
import { FEATURE_FLAGS, useFeatureFlags } from './feature-flags';
import { usePermissions } from './permissions';
import { showsNavigationItem, useAppliedAgencyProfile } from './agency-profile-access';
import { useOutboundCallsEnabled } from './use-outbound-calls-enabled';
import { useCanViewSettingsUsageTab } from './use-settings-usage-tab-enabled';
import {
  useTeamManagementEntitlement,
  useAgencyProfileManagementEntitlement
} from './visibility/use-visibility-entitlement';

/**
 * Everything a `hidden` predicate below is allowed to consult. Gathered once by
 * useNavigationAccess so the navigation itself stays module-level data: the
 * entries are declared before any provider is mounted, so nothing in scope
 * there can call a hook.
 */
export type NavigationAccess = {
  flags: ReadonlySet<string>;
  hasPermission: (resource: Resource, action: Action) => boolean;
  teamManagement: boolean;
  agencyProfileManagement: boolean;
  canViewUsageTab: boolean;
  outboundCalls: boolean;
  /**
   * Whether the signed-in user's profile puts this entry in their sidebar.
   * Always true for a user no profile reaches.
   */
  showsNavigationItem: (item: NavigationItem) => boolean;
};

/**
 * A sub-route of a page, rendered as a disclosure row beneath its page in the
 * sidebar. `href` is relative to the owning page, e.g. `styles` under `library`.
 */
export type NavigationTab = {
  name: string;
  href: string;
  /**
   * Subtitle for the page heading while this tab is selected. Every tab carries
   * one, so that switching tabs changes the whole heading rather than leaving a
   * subtitle describing the parent page beneath a title that has moved on.
   */
  description: string;
  /**
   * Extra terms universal search matches this tab on, for the words people
   * actually type. Nobody searching for "docx" types "Word Templates".
   */
  keywords?: string[];
  hidden?: (access: NavigationAccess) => boolean;
};

export type NavigationIcon = typeof HomeIcon;

export type NavigationPage = {
  name: string;
  href: string;
  icon: NavigationIcon;
  /** Filled variant of `icon`, drawn while this page (or its rail section) is the current one. */
  activeIcon: NavigationIcon;
  /**
   * Subtitle for the page heading. On a page with tabs this shows only while the
   * index redirect to the first tab is in flight, since each tab supplies its own.
   */
  description?: string;
  /**
   * Sub-routes surfaced in the sidebar under this page. A page that declares
   * tabs here must not also draw its own tab strip, or the same choice appears
   * twice on screen.
   */
  tabs?: NavigationTab[];
  /** Extra terms universal search matches this page on. See NavigationTab.keywords. */
  keywords?: string[];
  /**
   * Hides the sidebar entry when it returns true. Only the entry is hidden --
   * the route stays registered, so bookmarks and direct URLs keep working.
   */
  hidden?: (access: NavigationAccess) => boolean;
};

export type NavigationGroup = { name?: string; pages: NavigationPage[] };

/** Email templates follow role permissions alone, so no access profile hides them. */
const showsEmailTemplates = (access: NavigationAccess) =>
  access.flags.has(FEATURE_FLAGS.EMAIL_TEMPLATES) &&
  access.hasPermission(Resource.EmailTemplate, Action.List);

const libraryTabs: NavigationTab[] = [
  {
    name: 'Styles',
    href: 'styles',
    description: 'Created styles for the documents Cara generates.',
    keywords: ['branding', 'fonts', 'colors', 'letterhead', 'styles'],
    hidden: (access) => !access.showsNavigationItem(NavigationItem.LibraryStyles)
  },
  {
    name: 'Templates',
    href: 'templates',
    description: 'Reusable document templates for Cara to use when generating documents.',
    keywords: ['html', 'letter', 'document template'],
    hidden: (access) => !access.showsNavigationItem(NavigationItem.LibraryTemplates)
  },
  {
    name: 'Word Templates',
    href: 'word-templates',
    description: 'Uploaded .docx files Cara fills in to produce Word documents.',
    keywords: ['docx', 'word', 'microsoft'],
    hidden: (access) => !access.showsNavigationItem(NavigationItem.LibraryWordTemplates)
  },
  {
    name: 'Excel Templates',
    href: 'excel-templates',
    description: 'Uploaded .xlsx workbooks Cara fills in to produce spreadsheets.',
    keywords: ['xlsx', 'excel', 'spreadsheet', 'workbook'],
    hidden: (access) => !access.showsNavigationItem(NavigationItem.LibraryExcelTemplates)
  },
  {
    name: 'Email Templates',
    href: 'email-templates',
    description: 'Saved emails Cara fills in with client details when drafting.',
    keywords: ['email', 'subject line', 'mail', 'fields'],
    hidden: (access) => !showsEmailTemplates(access)
  },
  {
    name: 'Custom Forms',
    href: 'forms',
    description: 'PDF forms Cara extracts fields from and prepares for you.',
    keywords: ['pdf', 'fillable', 'scanned', 'supplemental'],
    hidden: (access) => !access.showsNavigationItem(NavigationItem.LibraryCustomForms)
  }
];

export const libraryPage: NavigationPage = {
  name: 'Library',
  href: 'library',
  icon: BuildingLibraryIcon,
  activeIcon: BuildingLibraryIconSolid,
  description:
    'Manage reusable templates and forms for Cara to reference when generating documents and emails',
  keywords: ['templates', 'styles', 'forms', 'branding', 'email'],
  tabs: libraryTabs,
  // Library owns no resource of its own, so it follows its tabs: a row that
  // opens onto only hidden tabs is a dead end.
  hidden: (access) => libraryTabs.every((tab) => tab.hidden?.(access) ?? false)
};

export const callsPage: NavigationPage = {
  name: 'Calls',
  href: 'calls',
  icon: PhoneIcon,
  activeIcon: PhoneIconSolid,
  description: 'Inbound and outbound calls handled by Cara',
  keywords: ['phone', 'voice', 'recordings', 'transcripts'],
  hidden: (access) => !access.showsNavigationItem(NavigationItem.Calls),
  tabs: [
    {
      name: 'Inbound',
      href: 'log',
      description: 'Inbound calls handled by Cara.',
      keywords: ['call log', 'received', 'voicemail'],
      hidden: (access) => !access.showsNavigationItem(NavigationItem.CallsInbound)
    },
    {
      name: 'Outbound',
      href: 'outbound',
      description: 'Outbound calls placed or scheduled by.',
      keywords: ['dial', 'placed', 'callback'],
      hidden: (access) =>
        !access.outboundCalls || !access.showsNavigationItem(NavigationItem.CallsOutbound)
    },
    {
      name: 'Report',
      href: 'report',
      description: 'Call volume across business hours, after hours and hangups.',
      keywords: ['analytics', 'volume', 'hangups'],
      hidden: (access) => !access.showsNavigationItem(NavigationItem.CallsReport)
    }
  ]
};

export const mailboxPage: NavigationPage = {
  name: 'Mailbox',
  href: 'mailbox',
  icon: EnvelopeIcon,
  activeIcon: EnvelopeIconSolid,
  description: 'Email Cara has taken part in, inbound and outgoing',
  keywords: ['inbox', 'mail', 'threads', 'messages', 'emails'],
  hidden: (access) => !access.showsNavigationItem(NavigationItem.Emails),
  // Not Inbound: the sidebar keeps every page's tabs mounted, so Calls already
  // has a link by that name in the same list.
  tabs: [
    {
      name: 'Inbox',
      href: 'inbox',
      description: 'Email conversations that Cara has been a part of.',
      keywords: ['inbound', 'threads', 'received', 'conversations']
    },
    {
      name: 'Outgoing',
      href: 'outgoing',
      description: 'Every email Cara has composed for you, from unsent drafts to what went out.',
      // Empty without a connected mailbox, unlike Inbox, whose threads predate them.
      hidden: (access) =>
        !access.flags.has(FEATURE_FLAGS.MAILBOX) ||
        !access.showsNavigationItem(NavigationItem.MailboxOutgoing),
      keywords: [
        'drafts',
        'sent',
        'outbox',
        'outbound',
        'send log',
        'unsent',
        'waiting',
        'delivery',
        'failed',
        'mailbox'
      ]
    }
  ]
};

export const triggersPage: NavigationPage = {
  name: 'Triggers',
  href: 'workflows',
  icon: BoltIcon,
  activeIcon: BoltIconSolid,
  description: 'Automations that launch Cara workspaces from email, schedules, and calls',
  keywords: ['workflows', 'automation', 'triggers', 'executions', 'gallery', 'runs'],
  hidden: (access) => !access.flags.has(FEATURE_FLAGS.SHOW_WORKFLOWS_TAB),
  tabs: [
    {
      name: 'Triggers',
      href: 'triggers',
      description: 'Email, schedule, and call events that launch a preset workspace.',
      keywords: ['automation', 'email trigger', 'schedule', 'cron']
    },
    {
      name: 'Executions',
      href: 'events',
      description: 'Recent trigger events with their evaluation and launch status.',
      keywords: ['runs', 'history', 'events', 'log']
    }
  ]
};

export const navigationGroups: NavigationGroup[] = [
  {
    pages: [
      {
        name: 'Home',
        href: 'home',
        icon: HomeIcon,
        activeIcon: HomeIconSolid,
        keywords: ['dashboard', 'start', 'overview']
      }
    ]
  },
  {
    name: 'Copilot',
    pages: [
      {
        name: 'Workspaces',
        href: 'workspaces',
        icon: SparklesIcon,
        activeIcon: SparklesIconSolid,
        keywords: ['copilot', 'tasks', 'runs', 'chat'],
        hidden: (access) => !access.showsNavigationItem(NavigationItem.Workspaces)
      },
      // Added alongside Accounts rather than replacing it, and on its own gate:
      // retiring the legacy pages is a separate decision from releasing this one,
      // so hiding Accounts early must not expose an unfinished Clients tab.
      //
      // Note the inverted predicate. SHOW_CLIENTS_TAB turns the feature on, so
      // the tab is hidden unless the gate is set -- the opposite polarity to
      // HIDE_ACCOUNTS_POLICIES_TABS on Accounts.
      {
        name: 'Clients',
        href: 'clients',
        icon: UserGroupIcon,
        activeIcon: UserGroupIconSolid,
        keywords: ['insureds', 'customers', 'contacts'],
        hidden: (access) =>
          !access.flags.has(FEATURE_FLAGS.SHOW_CLIENTS_TAB) ||
          !access.showsNavigationItem(NavigationItem.Clients)
      },
      {
        name: 'Presets',
        href: 'presets',
        icon: CubeIcon,
        activeIcon: CubeIconSolid,
        keywords: ['use cases', 'playbooks', 'prompts', 'automations'],
        hidden: (access) => !access.showsNavigationItem(NavigationItem.Presets)
      },
      libraryPage,
      {
        name: 'Documents',
        href: 'documents',
        icon: FolderIcon,
        activeIcon: FolderIconSolid,
        keywords: ['artifacts', 'outputs', 'files', 'downloads'],
        hidden: (access) => !access.showsNavigationItem(NavigationItem.Documents)
      },
      {
        name: 'Smart Fill',
        href: 'smart-fill',
        icon: ClipboardDocumentCheckIcon,
        activeIcon: ClipboardDocumentCheckIconSolid,
        keywords: ['acord', 'buckets', 'carrier sites', 'quotes', 'extension'],
        hidden: (access) => !access.flags.has(FEATURE_FLAGS.SMART_FILL_ACORD_BUCKETS)
      }
    ]
  },
  {
    name: 'Workflows',
    pages: [triggersPage]
  },
  {
    name: 'Servicing',
    pages: [
      {
        name: 'Service Requests',
        href: 'servicing',
        icon: TicketIcon,
        activeIcon: TicketIconSolid,
        keywords: ['tickets', 'servicing', 'endorsements', 'queue'],
        hidden: (access) => !access.showsNavigationItem(NavigationItem.Servicing)
      }
    ]
  },
  {
    name: 'Communication',
    pages: [callsPage, mailboxPage]
  },
  {
    name: 'Knowledge',
    pages: [
      {
        name: 'Knowledge Base',
        href: 'knowledge',
        icon: CircleStackIcon,
        activeIcon: CircleStackIconSolid,
        keywords: ['kb', 'reference', 'guidelines', 'appetite'],
        hidden: (access) => !access.showsNavigationItem(NavigationItem.KnowledgeBase)
      },
      {
        name: 'Accounts',
        href: 'accounts',
        icon: RectangleStackIcon,
        activeIcon: RectangleStackIconSolid,
        keywords: ['insureds', 'customers'],
        hidden: (access) => access.flags.has(FEATURE_FLAGS.HIDE_ACCOUNTS_POLICIES_TABS)
      },
      {
        name: 'Policies',
        href: 'policies',
        icon: DocumentTextIcon,
        activeIcon: DocumentTextIconSolid,
        keywords: ['coverage', 'lines of business', 'carriers'],
        // Same flag as Accounts: the two legacy pages are retired together.
        hidden: (access) => access.flags.has(FEATURE_FLAGS.HIDE_ACCOUNTS_POLICIES_TABS)
      }
    ]
  }
];

/**
 * Sits below the main groups, past the divider, rather than inside Knowledge.
 * Its tabs carry the permission gates that Settings' own tab strip used to
 * apply, so a user without the permission never sees the row.
 */
export const settingsPage: NavigationPage = {
  name: 'Settings',
  href: 'settings',
  icon: Cog6ToothIcon,
  activeIcon: Cog6ToothIconSolid,
  description: 'Configure and manage your account',
  keywords: ['preferences', 'configuration', 'admin'],
  tabs: [
    {
      name: 'Profile',
      href: 'profile',
      description: 'Your details, browser notifications, and producer information.',
      keywords: ['me', 'my account', 'notifications', 'password', 'signature']
    },
    {
      name: 'Agency',
      href: 'agency',
      description: 'Agency details, producer information, and ACORD form defaults.',
      keywords: ['company', 'organization', 'acord defaults', 'producer', 'license'],
      hidden: (access) => !access.hasPermission(Resource.Agency, Action.Update)
    },
    {
      name: 'Design',
      href: 'design',
      description: 'The designs your documents use: colors, typefaces, logos and pages.',
      keywords: [
        'brand',
        'designs',
        'colours',
        'colors',
        'palette',
        'typeface',
        'font',
        'logo',
        'theme'
      ],
      // Designs take the Styles permission; only choosing the default is for admins.
      hidden: (access) =>
        !access.flags.has(FEATURE_FLAGS.AGENCY_BRAND_DESIGN) ||
        !access.hasPermission(Resource.HtmlDocumentTemplate, Action.List)
    },
    {
      name: 'Analytics',
      href: 'analytics',
      description: 'How your team is using workspaces, calls, emails, and knowledge.',
      keywords: ['reports', 'metrics', 'adoption', 'activity'],
      hidden: (access) => !access.hasPermission(Resource.Analytics, Action.Read)
    },
    {
      name: 'Teams',
      href: 'teams',
      description: 'Groups of users, and who belongs to each one.',
      keywords: ['groups', 'members', 'users', 'roles', 'permissions'],
      // Without the entitlement the page is only an upgrade card, so the tab is
      // for admins, the only ones who can create teams, rather than every user
      // who can list them.
      hidden: (access) =>
        !access.hasPermission(Resource.Team, Action.List) ||
        (!access.teamManagement && !access.hasPermission(Resource.Team, Action.Create)) ||
        !access.showsNavigationItem(NavigationItem.SettingsTeams)
    },
    {
      name: 'Access Profiles',
      href: 'access-profiles',
      description: 'What each group of users sees when they sign in.',
      // "profile" is here so this still answers a search for it, since the tab
      // above is the one people mean by that word.
      keywords: ['roles', 'permissions', 'access', 'profile', 'home screen', 'tabs', 'groups'],
      // Admin-only. The tab stays visible without the entitlement so
      // an agency can read what profiles would do for them; the page itself
      // explains the plan.
      hidden: (access) => !access.hasPermission(Resource.AgencyProfile, Action.List)
    },
    {
      name: 'Integrations',
      href: 'integrations',
      description: 'Connect your AMS and CRM so Cara works from your own records.',
      keywords: [
        'ams',
        'crm',
        'connect',
        'hawksoft',
        'ezlynx',
        'nowcerts',
        'applied epic',
        'agencyzoom',
        'hubspot',
        'outlook',
        'gmail'
      ],
      hidden: (access) => !access.showsNavigationItem(NavigationItem.SettingsIntegrations)
    },
    {
      // Plural: a second row reading "Mailbox" would be indistinguishable from
      // the section by name alone.
      name: 'Mailboxes',
      href: 'mailbox',
      description: 'The mailboxes Cara sends your email from.',
      hidden: (access) =>
        (!access.flags.has(FEATURE_FLAGS.MAILBOX) &&
          !access.hasPermission(Resource.Mailbox, Action.Delete)) ||
        !access.showsNavigationItem(NavigationItem.SettingsMailboxes),
      keywords: ['email', 'outlook', 'microsoft', 'connect', 'send', 'inbox']
    },
    {
      name: 'Voice AI',
      href: 'voice',
      description: 'Voice agents that answer your phones.',
      keywords: ['phone', 'greeting', 'after hours', 'escalation', 'transfer', 'receptionist'],
      hidden: (access) => !access.showsNavigationItem(NavigationItem.SettingsVoice)
    },
    {
      name: 'Memory',
      href: 'memory',
      description: 'Saved facts Cara automatically curates and brings into workspaces.',
      keywords: ['facts', 'context', 'remember', 'preferences'],
      hidden: (access) => !access.showsNavigationItem(NavigationItem.SettingsMemory)
    },
    {
      name: 'Email',
      href: 'email',
      description: 'How Cara writes emails for your agency.',
      keywords: ['guidelines', 'mail', 'writing', 'tone'],
      hidden: (access) =>
        !access.flags.has(FEATURE_FLAGS.EMAIL_TEMPLATES) ||
        !access.hasPermission(Resource.Agency, Action.Update)
    },
    {
      name: 'Usage',
      href: 'usage',
      description: 'Platform units, knowledge base pages, and voice minutes consumed.',
      keywords: ['consumption', 'units', 'minutes', 'credits'],
      hidden: (access) => !access.canViewUsageTab
    },
    {
      name: 'Limits',
      href: 'limits',
      description: 'Overage caps for each usage meter.',
      keywords: ['caps', 'overage', 'quota', 'budget'],
      hidden: (access) => !access.canViewUsageTab
    },
    {
      name: 'Subscription',
      href: 'subscription',
      description: 'Your plan, seats, and billing period.',
      keywords: ['billing', 'plan', 'seats', 'invoice', 'payment', 'upgrade'],
      hidden: (access) =>
        !access.hasPermission(Resource.Subscription, Action.Update) &&
        !access.hasPermission(Resource.AgencyUser, Action.Create)
    }
  ]
};

export const filterNavigationTabs = (tabs: NavigationTab[], access: NavigationAccess) =>
  tabs.filter((tab) => !tab.hidden?.(access));

/**
 * Applies each entry's `hidden` predicate, dropping any group left empty as a
 * result so no header is orphaned above nothing. Tabs are filtered separately,
 * by filterNavigationTabs, since a page stays reachable even when every one of
 * its tabs is hidden.
 */
export const filterNavigationGroups = (groups: NavigationGroup[], access: NavigationAccess) =>
  groups
    .map((group) => ({
      ...group,
      pages: group.pages.filter((page) => !page.hidden?.(access))
    }))
    .filter((group) => group.pages.length > 0);

export const useNavigationAccess = (): NavigationAccess => {
  const flags = useFeatureFlags();
  const { hasPermission } = usePermissions();
  const teamManagement = useTeamManagementEntitlement();
  const agencyProfileManagement = useAgencyProfileManagementEntitlement();
  const canViewUsageTab = useCanViewSettingsUsageTab();
  const outboundCalls = useOutboundCallsEnabled();
  const profile = useAppliedAgencyProfile();

  // A profile stops narrowing the sidebar the moment the plan drops it, so a
  // downgraded agency is not left navigating a shape it can no longer edit.
  const appliedProfile = agencyProfileManagement ? profile : null;
  const showsItem = React.useCallback(
    (item: NavigationItem) => showsNavigationItem(appliedProfile, item),
    [appliedProfile]
  );

  return React.useMemo(
    () => ({
      flags,
      hasPermission,
      teamManagement,
      agencyProfileManagement,
      canViewUsageTab,
      outboundCalls,
      showsNavigationItem: showsItem
    }),
    [
      flags,
      hasPermission,
      teamManagement,
      agencyProfileManagement,
      canViewUsageTab,
      outboundCalls,
      showsItem
    ]
  );
};

/**
 * Tests an href the same way NavLink does -- a prefix match on whole segments,
 * relative to the calling route -- so the sidebar's disclosure opens for exactly
 * the row NavLink marks active.
 */
export const useIsHrefActive = () => {
  const { pathname } = useLocation();
  const base = useResolvedPath('.').pathname;

  return React.useCallback(
    (href: string) =>
      !!matchPath({ path: `${base}/${href}`.replace(/\/{2,}/g, '/'), end: false }, pathname),
    [base, pathname]
  );
};

/**
 * Title and subtitle for a page whose tab strip moved into the sidebar, taken
 * from the tab the URL is inside. Both move together: a title that follows the
 * tab under a subtitle that still describes the parent reads as a mistake.
 *
 * Falls back to the page while the index redirect to the first tab is in flight.
 */
export const usePageHeading = (page: NavigationPage): { title: string; description?: string } => {
  const isHrefActive = useIsHrefActive();
  const tab = (page.tabs ?? []).find((candidate) => isHrefActive(candidate.href));

  return tab
    ? { title: tab.name, description: tab.description }
    : { title: page.name, description: page.description };
};
